import { useEffect } from 'react';
import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { TextInput } from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import AddTaskPopover from './addTaskPopover';
import useGetDragAndDropApi from '../hooks/useGetDragAndDropData';
import useAddNewBucket from '../hooks/useAddNewBucket';
import useUpdateTaskBucket from '../hooks/useUpdateTaskBucket';

import { ReactComponent as CalendarIcon } from 'assets/icons/calenderIcon.svg';
import { ReactComponent as ListIcon } from 'assets/icons/listIcon.svg';
import { ReactComponent as CommentsIcon } from 'assets/icons/commentsIcon.svg';
import { ReactComponent as ChainIcon } from 'assets/icons/chainIcon.svg';
import EditTaskModal from './editTaskModal';
import SingleCheckListOnTask from './singleCheckListonCard';

export default function DrapAndDrop() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  
  // initial state
  const [stateData, setStateData] = useState([]);
  const [selectedBucketData, setSelectedBucket] = useState('');
  const [isAddPopoverOpen, setisAddPopoverOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTaskData, setSelectedTaskData] = useState('');

  const defaultValues = {
    name: '',
  };

  const { handleSubmit, control, reset,watch,formState:{errors}} = useForm({ defaultValues });

  const getDragAndDropData = useGetDragAndDropApi(
    id,
    searchParams.get('filter')
  );
  const addNewBucketMutation = useAddNewBucket(reset);
  const updateBucketMutation = useUpdateTaskBucket();
  const getErrorMessage = (name) => {
    return (
      errors[name] && (
        <p className=' text-xs font-semibold text-red-500'>
          {errors[name]?.message}
        </p>
      )
    );
  };


  // function to handle movement of an item from one bucket to another
  const move = (
    source,
    destination,
    droppableSource,
    droppableDestination,
    name
  ) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination.index, 0, removed);
    const result = {};
    // updating progress when the task moved
    if (searchParams?.get('filter') === 'progress') {
      destClone?.map((item) => {
        if (item?.id === removed?.id) {
          item['progress'] = name;
        }
        return true;
      });
    }
    // updating time when ever task moved
    if (searchParams?.get('filter') === 'due_date') {
      destClone?.map((item) => {
        if (item?.id === removed?.id) {
          item['end_date'] =
            name === 'Today'
              ? new Date()
              : name === 'Late'
              ? dayjs(new Date()).subtract(1, 'day')?.$d
              : name === 'Tomorrow'
              ? dayjs(new Date()).add(1, 'day')?.$d
              : name === 'This Week'
              ? dayjs(new Date(dayjs(new Date()).endOf('week')?.$d)).add(
                  1,
                  'day'
                )
              : name === 'Next Week'
              ? dayjs(
                  new Date(
                    dayjs(
                      new Date(dayjs(new Date()).add(1, 'week')?.$d)
                    ).startOf('week')?.$d
                  )
                ).add(1, 'day')
              : name === 'Future'
              ? dayjs(
                  new Date(
                    dayjs(
                      new Date(dayjs(new Date()).add(2, 'week')?.$d)
                    ).startOf('week')?.$d
                  )
                )?.add(1, 'day')
              : '';
        }
        return true;
      });
    }

    // code to remove duplicate tasks from destination bucket
    let uniqueDestClone = [];
    if (searchParams.get('filter') === 'assigned_to') {
      const uniqueIds = [];
      uniqueDestClone = destClone.filter((element) => {
        const isDuplicate = uniqueIds.includes(element.id);
        if (!isDuplicate) {
          uniqueIds.push(element.id);
          return true;
        }
        return false;
      });
    }

    result[droppableSource.droppableId] = sourceClone;
    result[droppableDestination.droppableId] =
      searchParams.get('filter') === 'assigned_to'
        ? uniqueDestClone
        : destClone;

    return result;
  };

  // function to handle reorder of items
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  // end of the drag to set state with updated values
  function onDragEnd(result) {
    const { source, destination } = result;

    // dropped outside of the drag area
    if (!destination) {
      return;
    }
    const sInd = +source.droppableId;
    const dInd = +destination.droppableId;

    const destinationBucketId = stateData[dInd]?.bucket?.bucket_id;

    // if a task is just reorded in the same bucket
    if (sInd === dInd) {
      let items;
      if (searchParams?.get('filter') !== 'due_date') {
        items = reorder(
          stateData[sInd]?.tasks,
          source.index,
          destination.index
        );
      }
      const newState = [...stateData];
      if (searchParams?.get('filter') !== 'due_date') {
        newState[sInd]['tasks'] = items;
      }
      setStateData(newState);

      // put requests for different filters to update in the backend
      if (searchParams.get('filter') === 'bucket') {
        updateBucketMutation.mutate({
          filter_type: 'bucket',
          ordering_list: newState[sInd]?.tasks?.map((item) => item?.id),
          bucket_id: newState[sInd]?.bucket?.bucket_id,
        });
      } else if (searchParams.get('filter') === 'assigned_to') {
        updateBucketMutation.mutate({
          filter_type: 'assigned_to',
          ordering_list: newState[sInd]?.tasks?.map((item) => item?.id),
        });
      } else if (searchParams.get('filter') === 'progress') {
        updateBucketMutation.mutate({
          filter_type: 'progress',
          ordering_list: newState[sInd]?.tasks?.map((item) => item?.id),
          progress: newState[sInd]?.progress.name,
        });
      } else if (searchParams.get('filter') === 'priority') {
        updateBucketMutation.mutate({
          filter_type: 'priority',
          ordering_list: newState[sInd]?.tasks?.map((item) => item?.id),
          priority: newState[sInd]?.priority.name,
        });
      } else {
        return null;
      }
    } else {
      // if the task is moved to different bucket
      const result = move(
        stateData[sInd]?.tasks,
        stateData[dInd]?.tasks,
        source,
        destination,
        stateData[dInd]?.progress?.name || stateData[dInd]?.due_date?.name
      );

      const [removedTask] = stateData[sInd]?.tasks.splice(source.index, 1);

      const newState = [...stateData];
      newState[sInd]['tasks'] = result[sInd];
      newState[dInd]['tasks'] = result[dInd];

      setStateData(newState);

      // put requests for different filters to update in the backend
      if (searchParams.get('filter') === 'bucket') {
        updateBucketMutation.mutate({
          filter_type: 'bucket',
          ordering_list: result[dInd]?.map((item) => item?.id),
          bucket_id: destinationBucketId,
        });
      } else if (searchParams.get('filter') === 'assigned_to') {
        updateBucketMutation.mutate({
          filter_type: 'assigned_to',
          ordering_list: result[dInd]?.map((item) => item?.id),
          removed_from: newState[sInd]?.assignee?.employee_id,
          added_to: newState[dInd]?.assignee?.employee_id,
          task_id: removedTask?.id,
        });
      } else if (searchParams.get('filter') === 'progress') {
        updateBucketMutation.mutate({
          filter_type: 'progress',
          ordering_list: result[dInd]?.map((item) => item?.id),
          progress: newState[dInd]?.progress?.name,
        });
      } else if (searchParams.get('filter') === 'priority') {
        updateBucketMutation.mutate({
          filter_type: 'priority',
          ordering_list: result[dInd]?.map((item) => item?.id),
          priority: newState[dInd]?.priority?.name,
        });
      } else {
        updateBucketMutation.mutate({
          filter_type: 'due_date',
          to_time:
            newState[dInd]?.due_date?.name === 'Late'
              ? 'late'
              : newState[dInd]?.due_date?.name === 'Today'
              ? 'today'
              : newState[dInd]?.due_date?.name === 'Tomorrow'
              ? 'tomorrow'
              : newState[dInd]?.due_date?.name === 'This Week'
              ? 'this_week'
              : newState[dInd]?.due_date?.name === 'Next Week'
              ? 'next_week'
              : newState[dInd]?.due_date?.name === 'Future'
              ? 'future'
              : 'no_due',
          task_id: removedTask?.id,
        });
      }
    }
  }

  const grid = 8;

  // task card styles while dragging
  const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    padding: grid * 2,
    // change background colour if dragging
    background: isDragging ? '#FEECE6' : 'white',
    // styles we need to apply on draggables
    ...draggableStyle,
  });

  // handle create bucket form submit
  const onSubmit = (data) => {
    const finalData = {
      task_board_id: id,
      name: data?.name,
    };
    addNewBucketMutation.mutate(finalData);
  };

  useEffect(() => {
    setStateData(getDragAndDropData?.data?.data);
  }, [getDragAndDropData?.data?.data]);

  // rendering add task popover based on different filters
  const renderAddTaskPopover = (item) => {
    switch (searchParams.get('filter')) {
      case 'bucket':
        if (selectedBucketData?.bucket?.bucket_id === item?.bucket?.bucket_id) {
          return (
            <div className='border mx-2 border-gray border-opacity-10 mb-8 shadow-lg shadow-lightShadow p-4 bg-white'>
              <AddTaskPopover
                bucketDetails={selectedBucketData}
                setisAddPopoverOpen={setisAddPopoverOpen}
                name='bucket'
              />
            </div>
          );
        }
        break;

      case 'assigned_to':
        if (
          selectedBucketData?.assignee?.employee_id ===
          item?.assignee?.employee_id
        ) {
          return (
            <div className='border mx-2 border-gray border-opacity-10 mb-8 shadow-lg shadow-lightShadow p-4 bg-white'>
              <AddTaskPopover
                bucketDetails={selectedBucketData}
                setisAddPopoverOpen={setisAddPopoverOpen}
                name='assigned_to'
              />
            </div>
          );
        }
        break;

      case 'progress':
        if (selectedBucketData?.progress?.name === item?.progress?.name) {
          return (
            <div className='border mx-2 border-gray border-opacity-10 mb-8 shadow-lg shadow-lightShadow p-4 bg-white'>
              <AddTaskPopover
                bucketDetails={selectedBucketData}
                setisAddPopoverOpen={setisAddPopoverOpen}
                name='progress'
              />
            </div>
          );
        }
        break;

      case 'priority':
        if (selectedBucketData?.priority?.name === item?.priority?.name) {
          return (
            <div className='border mx-2 border-gray border-opacity-10 mb-8 shadow-lg shadow-lightShadow p-4 bg-white'>
              <AddTaskPopover
                bucketDetails={selectedBucketData}
                setisAddPopoverOpen={setisAddPopoverOpen}
                name='priority'
              />
            </div>
          );
        }
        break;

      case 'due_date':
        if (selectedBucketData?.due_date?.name === item?.due_date?.name) {
          return (
            <div className='border mx-2 border-gray border-opacity-10 mb-8 shadow-lg shadow-lightShadow p-4 bg-white'>
              <AddTaskPopover
                bucketDetails={selectedBucketData}
                setisAddPopoverOpen={setisAddPopoverOpen}
                name='due_date'
              />
            </div>
          );
        }
        break;

      default:
        return null;
    }
  };

  return (
    <div className='h-full pb-4'>
      <div className='flex overflow-x-scroll mx-4 scroller mb-2 h-full'>
        <DragDropContext onDragEnd={onDragEnd}>
          {stateData &&
            stateData.map((item, ind) => {
              return (
                <div key={ind}>
                  <div className='mx-2 mb-1 text-lg font-semibold mt-4'>
                    <p className='pl-4'>
                      {searchParams.get('filter') === 'bucket'
                        ? item?.bucket?.name
                        : searchParams.get('filter') === 'assigned_to'
                        ? item?.assignee?.name
                        : searchParams.get('filter') === 'progress'
                        ? item?.progress?.name
                        : searchParams.get('filter') === 'priority'
                        ? item?.priority?.name
                        : item?.due_date?.name}
                    </p>
                  </div>
                  <div className='bg-[#F9F4F4] mx-2 mb-3 p-4 flex items-center justify-between rounded-lg relative'>
                    <p className='text-md'>Add task</p>
                    <span
                      className='bg-primary text-white w-7 h-7 text-center font-bold  rounded-full select-none cursor-pointer'
                      onClick={() => {
                        setSelectedBucket(item);
                        setisAddPopoverOpen(true);
                      }}
                    >
                      +
                    </span>
                  </div>
                  <Droppable droppableId={`${ind}`}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className='pt-0 w-[340px]'
                      >
                        {/* each task card */}
                        <div className='bg-[#F9F4F4] p-4 mx-2 rounded-lg pt-6 min-h-[70vh]'>
                          {/* add task popover */}
                          {isAddPopoverOpen ? (
                            <>{renderAddTaskPopover(item)}</>
                          ) : null}
                          {item?.tasks?.map((item1, index) => {
                            return (
                              <Draggable
                                key={`${item1.id}`}
                                draggableId={`${item?.assignee?.employee_id}${item1.id}`}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={getItemStyle(
                                      snapshot.isDragging,
                                      provided.draggableProps.style
                                    )}
                                    className='shadow-light Shadow shadow-lg mb-7 mx-2 border border-gray border-opacity-10'
                                  >
                                    <div>
                                      <p
                                        className='text-base text-333333 font-bold truncate w-40'
                                        onClick={() => {
                                          setSelectedTaskData(item1);
                                          setIsEditModalOpen(true);
                                        }}
                                      >
                                        {item1.name}
                                      </p>
                                      <div
                                        className='flex mt-3 items-center justify-between'
                                        onClick={() => {
                                          setSelectedTaskData(item1);
                                          setIsEditModalOpen(true);
                                        }}
                                      >
                                        <p className='text-primary text-sm'>
                                          {item1?.progress}
                                        </p>
                                        <div className='flex items-center space-x-3 text-sm'>
                                          <div className='flex items-center space-x-1 text-gray3'>
                                            <ListIcon className='w-4' />
                                            <p>
                                              {item1?.completed_checklist_count}{' '}
                                              / {item1?.total_checklist_count}
                                            </p>
                                          </div>
                                          <div className='flex items-center space-x-1 text-gray3'>
                                            <ChainIcon className='w-4' />
                                            <p>{item1.total_attachments}</p>
                                          </div>
                                          <div className='flex items-center space-x-1 text-gray3'>
                                            <CommentsIcon className='w-4' />
                                            <p>{item1.total_comments}</p>
                                          </div>
                                        </div>
                                      </div>
                                      {item1?.show_checklist_on_card ? (
                                        <div className='mt-3'>
                                          {item1?.checklist?.map(
                                            (singleitem) => {
                                              if (!singleitem?.is_completed) {
                                                return (
                                                  <SingleCheckListOnTask
                                                    singleitem={singleitem}
                                                    item1={item1}
                                                  />
                                                );
                                              }
                                              return true;
                                            }
                                          )}
                                        </div>
                                      ) : null}
                                      <div
                                        className='mt-3 flex items-center justify-between text-sm'
                                        onClick={() => {
                                          setSelectedTaskData(item1);
                                          setIsEditModalOpen(true);
                                        }}
                                      >
                                        {item1?.end_date && (
                                          <div className='flex items-center space-x-2 text-gray3'>
                                            <CalendarIcon className='w-5' />
                                            <p>
                                              {dayjs(item1?.end_date).format(
                                                'DD MMM YYYY'
                                              )}
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                        </div>
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
        </DragDropContext>
        {searchParams.get('filter') === 'bucket' ? (
          <div className='mt-3 ml-4'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                control={control}
                name='name'
                rules={{ required: 'Required',validate:()=>watch('name').length<249||"maximum length exceded (250 chars)"} }
                render={({ field }) => (
                  <TextInput
                    {...field}
                    placeholder='Type & enter to add a bucket'
                    className='w-48'
                    classNames={{
                      input: 'border-none px-0',
                      wrapper: 'border-b-2 border-primary ',
                    }}
                  />
                )}
              />
             { getErrorMessage('name')}
            </form>
          </div>
        ) : null}
      </div>
      {isEditModalOpen && (
        <EditTaskModal
          isModalOpen={isEditModalOpen}
          setIsModalOpen={setIsEditModalOpen}
          selectedTaskData={selectedTaskData}
          setSelectedTaskData={setSelectedTaskData}
        />
      )}
    </div>
  );
}
